/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { isFocusVisible } from '@react-aria/interactions';
import clsx from 'clsx';
import { AnimatePresence, type HTMLMotionProps, motion, useReducedMotion } from 'motion/react';
import {
    type ComponentPropsWithRef,
    type CSSProperties,
    createContext,
    type ForwardedRef,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { type ContextValue, HeadingContext, useContextProps } from 'react-aria-components';

import { useEventCallback } from '@gitbook/util-react-hooks';

import { AnimatedStateContext } from '../animations';
import { Backdrop } from '../Backdrop';
import { AtomButton } from '../Button';
import { Overlay, OverlayRoot } from '../Overlay';
import { getDefaultPortalContainer, Portal, PortalContext } from '../Portal';
import { usePanelTransition } from '../SidePanel';
import { Tooltip, TooltipContext } from '../Tooltip';
import { useCheckIsPopoverOpen } from '../util';

export interface SidebarProps extends HTMLMotionProps<'aside'> {
    contained?: boolean;
}

export const SidebarContext = createContext<ContextValue<SidebarProps, HTMLDivElement>>({});

const SIDEBAR_MAX_WIDTH = 320;
export const SIDEBAR_INITIAL_WIDTH = 256;
const SIDEBAR_MIN_WIDTH = 200;

/**
 * Sidebar root component.
 */
export function Sidebar({
    ref,
    ...props
}: Omit<SidebarProps, 'ref'> & { ref?: ForwardedRef<HTMLDivElement> }) {
    [props, ref] = useContextProps(props, ref, SidebarContext);
    const { contained, ...rest } = props;
    return (
        <HeadingContext.Provider value={{}}>
            <motion.aside
                ref={ref}
                {...props}
                className={clsx(
                    'group/sidebar z-20 flex w-full max-w-[var(--sidebar-max-width,16rem)] flex-col justify-between gap-4 overflow-hidden bg-side py-3 select-none',
                    contained ? 'rounded-lg border border-control' : null,
                    rest.className
                )}
            />
        </HeadingContext.Provider>
    );
}

export function ResizableSidebar(props: {
    children: ReactNode;
    isCollapsed: boolean;
    onCollapseChange: (collapsed: boolean) => void;
    width: number | null;
    onWidthChange: (width: number) => void;
    expandMode: 'manual' | 'auto';
    className?: string;
}) {
    const {
        isCollapsed,
        onCollapseChange: setCollapse,
        onWidthChange: setWidth,
        children,
        expandMode = 'manual',
        className,
    } = props;
    const width = props.width ?? SIDEBAR_INITIAL_WIDTH;
    const [isResizing, setResizing] = useState(false);
    const [isOpen, setOpen] = useState(false);
    const [isResizeHandleHovered, setResizeHandleHovered] = useState(false);
    const [resizeHandleCursorY, setResizeHandleCursorY] = useState(0);
    const prefersReducedMotion = useReducedMotion();
    /**
     * Lock the opening of the sidebar, after a collapse.
     */
    const isOpenLocked = useRef(false);
    const collapse = useEventCallback(() => {
        if (!isCollapsed) {
            setCollapse(true);
            isOpenLocked.current = true;
        }
    });
    const rafRef = useRef<number | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clear = useEventCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    });
    useEffect(() => clear(), [clear]);
    const uncollapse = useEventCallback(() => {
        if (isCollapsed) {
            setCollapse(false);
            isOpenLocked.current = false;

            clear();
            setOpen(false);
        }
    });
    const checkIsPopoverOpen = useCheckIsPopoverOpen({ isDisabled: !isOpen });
    const close = useEventCallback((options?: { delay: number }) => {
        clear();
        rafRef.current = requestAnimationFrame(() => {
            if (checkIsPopoverOpen({ onClose: () => close(options) })) {
                return;
            }
            const delay = options?.delay ?? (prefersReducedMotion ? 0 : 75);
            if (delay === 0) {
                setOpen(false);
                return;
            }
            timeoutRef.current = setTimeout(() => {
                setOpen(false);
            }, delay);
        });
    });
    const open = useEventCallback(() => {
        if (isCollapsed && !isOpenLocked.current) {
            clear();
            if (!process.env.GITBOOK_PREDICTABLE_UI) {
                setOpen(true);
            }
        }
    });
    useEffect(() => {
        if (isResizing) {
            document.body.classList.add('resizing-sidebar');

            let moved = false;
            const handleMouseMove = (event) => {
                const x = event.clientX;
                moved = true;
                setWidth(Math.max(Math.min(x, SIDEBAR_MAX_WIDTH), SIDEBAR_MIN_WIDTH));
                if (x < 100) {
                    collapse();
                } else {
                    uncollapse();
                }
            };
            document.addEventListener('mousemove', handleMouseMove);

            const handleMouseUp = () => {
                setResizing(false);
                if (!moved) {
                    collapse();
                }
            };
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.body.classList.remove('resizing-sidebar');
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isResizing, collapse, uncollapse, setWidth]);
    useEffect(() => {
        if (isOpen) {
            const handleMouseOut = (event: MouseEvent) => {
                if (
                    event.clientY <= 0 ||
                    event.clientX <= 0 ||
                    event.clientX >= window.innerWidth ||
                    event.clientY >= window.innerHeight
                ) {
                    close();
                }
            };
            document.addEventListener('mouseout', handleMouseOut);
            return () => {
                document.removeEventListener('mouseout', handleMouseOut);
            };
        }
    }, [isOpen, close]);
    const transition = usePanelTransition();
    const portalContainerRef = useRef<HTMLDivElement>(null);
    const resizeHandleRef = useRef<HTMLDivElement>(null);
    const getPortalContainer = useCallback(
        () => portalContainerRef.current ?? getDefaultPortalContainer(),
        []
    );
    return (
        <PortalContext.Provider value={getPortalContainer}>
            {/* The wrapper is what the page beside it makes room for, so its width has to travel at
                the pace the sidebar slides: the sidebar inside is taken out of the flow the moment it
                starts to collapse, and left to itself the wrapper would drop to nothing at once,
                letting the page snap wide while the sidebar was still on its way out — and snap
                narrow again at the very end of the way back in. Instant while the user is dragging
                the edge, where following the pointer is the whole point. */}
            <motion.div
                data-spine-sidebar=""
                aria-expanded={!isCollapsed}
                className={clsx(
                    'group/resizable-sidebar relative z-20 flex min-h-0 flex-col has-[[data-backdrop]]:z-sidebar has-[[data-spine-popover]]:z-sidebar',
                    !isCollapsed && 'sidebar-expanded',
                    className
                )}
                initial={false}
                animate={{ width: isCollapsed ? 0 : width }}
                transition={isResizing ? { duration: 0 } : transition}
                style={
                    {
                        '--sidebar-width': `${width}px`,
                        '--sidebar-max-width': SIDEBAR_MAX_WIDTH,
                    } as CSSProperties
                }
            >
                <motion.div
                    className="z-20 flex min-h-0 w-(--sidebar-width) flex-1"
                    initial={false}
                    transition={transition}
                    style={{ position: 'relative' }}
                    animate={
                        isCollapsed
                            ? isOpen
                                ? {
                                      position: 'absolute',
                                      top: '0.75rem',
                                      bottom: '0.75rem',
                                      translateX: 0,
                                      left: '0.75rem',
                                  }
                                : {
                                      position: 'absolute',
                                      top: '0.75rem',
                                      bottom: '0.75rem',
                                      translateX: '-100%',
                                      left: 0,
                                  }
                            : {
                                  position: 'absolute',
                                  top: 0,
                                  bottom: 0,
                                  translateX: 0,
                                  left: 0,
                                  transitionEnd: {
                                      position: 'relative',
                                  },
                              }
                    }
                    onMouseEnter={() => open()}
                    onMouseLeave={() => close()}
                    onAnimationComplete={() => {
                        isOpenLocked.current = false;
                    }}
                    onFocus={() => {
                        if (isFocusVisible()) {
                            open();
                        }
                    }}
                >
                    <TooltipContext.Provider value={{ disabled: isCollapsed && !isOpen }}>
                        <SidebarContext.Provider value={{ contained: isCollapsed }}>
                            {children}
                        </SidebarContext.Provider>
                    </TooltipContext.Provider>
                    {!isCollapsed && (
                        <>
                            <Tooltip
                                label="Drag to resize"
                                description="Click to collapse"
                                position="right"
                                disabled={isResizing}
                                isOpen={isResizeHandleHovered && !isResizing}
                            >
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute -right-1 z-20 h-1 w-[7px]"
                                    style={{ top: `${resizeHandleCursorY}px` }}
                                />
                            </Tooltip>
                            <div
                                ref={resizeHandleRef}
                                onMouseDown={() => {
                                    setResizing(true);
                                }}
                                onMouseUp={() => {
                                    if (!isResizing) {
                                        collapse();
                                    }
                                }}
                                onMouseEnter={(event) => {
                                    const top = event.currentTarget.getBoundingClientRect().top;
                                    setResizeHandleHovered(true);
                                    setResizeHandleCursorY(event.clientY - top);
                                }}
                                onMouseLeave={() => {
                                    setResizeHandleHovered(false);
                                }}
                                onMouseMove={(event) => {
                                    if (!resizeHandleRef.current) {
                                        return;
                                    }
                                    const top = resizeHandleRef.current.getBoundingClientRect().top;
                                    setResizeHandleCursorY(event.clientY - top);
                                }}
                                className="group absolute inset-y-4 -right-1 z-20 flex w-[7px] cursor-col-resize justify-center"
                            >
                                <div className="w-[2px] rounded-sm transition group-hover:bg-neutral-solid" />
                            </div>
                        </>
                    )}
                </motion.div>
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            data-backdrop
                            className="fixed inset-0 bg-[rgb(38_41_48_/_0.5)] fill-mode-forwards"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={transition}
                            onClick={() => {
                                close({ delay: 0 });
                            }}
                        />
                    )}
                </AnimatePresence>
                {/* While we animate the sidebar we want to also animate the width of the sidebar. */}
                <motion.div
                    transition={transition}
                    initial={false}
                    animate={isCollapsed ? { width: 0 } : { width: 'var(--sidebar-width)' }}
                />
                {isCollapsed ? (
                    <div
                        className="group absolute inset-y-0 left-0 z-20 w-2"
                        onMouseEnter={() => {
                            if (expandMode === 'auto') {
                                open();
                            }
                        }}
                    >
                        {expandMode === 'manual' ? (
                            <Tooltip label="Expand sidebar" position="right">
                                <AtomButton
                                    className="pointer-events-none left-0 h-full w-4 -translate-x-2 bg-neutral-ui opacity-0 transition group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:cursor-pointer group-hover:opacity-100"
                                    onPress={() => {
                                        uncollapse();
                                    }}
                                />
                            </Tooltip>
                        ) : null}
                    </div>
                ) : null}
                {isOpen && (
                    <div
                        className="absolute top-0 left-0 z-20 h-3 w-60"
                        onClick={() => uncollapse()}
                        onMouseEnter={() => open()}
                        onMouseLeave={() => close()}
                    />
                )}
                <div ref={portalContainerRef} />
            </motion.div>
        </PortalContext.Provider>
    );
}

export function CollapsibleSidebar(props: {
    children: ReactNode;
    isOpen: boolean;
    onOpenChange: (opened: boolean) => void;
}) {
    const { isOpen, onOpenChange, children } = props;
    return (
        <OverlayRoot isOpen={isOpen} onOpenChange={onOpenChange}>
            <CollapsibleSidebarOverlay>{children}</CollapsibleSidebarOverlay>
        </OverlayRoot>
    );
}

function CollapsibleSidebarOverlay(props: { children: ReactNode }) {
    const { children } = props;
    const animatedState = useContext(AnimatedStateContext);

    const isExited = animatedState === 'exited';
    const isExiting = animatedState === 'exiting';
    const isEntering = animatedState === 'entering';

    return (
        <Portal>
            <Backdrop
                isExiting={isExiting}
                isEntering={isEntering}
                className={clsx('z-overlay', isExited && 'hidden')}
            />
            <Overlay
                className={clsx(
                    'absolute inset-y-0 left-0 z-overlay flex',
                    '[transition-duration:120ms] [transition-timing-function:ease-out] fill-mode-forwards',
                    isExited && 'hidden',
                    isEntering && 'animate-in fade-in slide-in-from-left-[10rem]',
                    isExiting && 'animate-out fade-out slide-out-to-left-[10rem]'
                )}
            >
                {children}
            </Overlay>
        </Portal>
    );
}

export interface SidebarBodyProps extends ComponentPropsWithRef<'div'> {
    /**
     * When true, removes the body's own horizontal padding so that Dividers span
     * the full sidebar width. Content components between Dividers should receive
     * their own `fullWidth` prop to restore inset padding.
     */
    fullWidth?: boolean;
}

/**
 * Sidebar body component.
 */
export function SidebarBody({ ref, ...props }: SidebarBodyProps) {
    const { fullWidth, ...rest } = props;
    return (
        <div
            ref={ref}
            {...rest}
            className={clsx(
                'relative flex scrollbar w-full flex-1 flex-col items-stretch gap-5 overflow-x-hidden overflow-y-auto',
                fullWidth ? '[scrollbar-gutter:auto]' : 'px-3 pr-1.5',
                rest.className
            )}
        >
            {rest.children}
        </div>
    );
}

export interface SidebarFooterProps extends ComponentPropsWithRef<'footer'> {}

/**
 * Sidebar footer component.
 */
export function SidebarFooter({ ref, ...props }: SidebarFooterProps) {
    return (
        <footer
            ref={ref}
            {...props}
            className={clsx('flex shrink-0 flex-col items-stretch gap-2 px-3', props.className)}
        />
    );
}

export interface SidebarHeaderProps extends ComponentPropsWithRef<'header'> {}

/**
 * Sidebar header component.
 */
export function SidebarHeader({ ref, ...props }: SidebarHeaderProps) {
    const { children, ...rest } = props;
    return (
        <header
            ref={ref}
            {...rest}
            className={clsx('flex shrink-0 items-center px-3', rest.className)}
        >
            <HeadingContext.Provider
                value={{ level: 1, className: 'truncate text-ui-heading-small' }}
            >
                {children}
            </HeadingContext.Provider>
        </header>
    );
}

export interface SidebarItemListProps extends ComponentPropsWithRef<'div'> {
    /**
     * When true, applies the standard sidebar horizontal padding (`px-3 pr-1.5`)
     * to this list. Use alongside `SidebarBody fullWidth` so items retain their
     * inset while Dividers span the full width.
     */
    fullWidth?: boolean;
}

/**
 * Sidebar item list component.
 */
export function SidebarItemList({ ref, ...props }: SidebarItemListProps) {
    const { fullWidth, ...rest } = props;
    return (
        <div
            ref={ref}
            {...rest}
            className={clsx('flex flex-col gap-px', fullWidth && 'px-3 pr-1.5', rest.className)}
        />
    );
}

export interface SidebarEmptyState extends ComponentPropsWithRef<'p'> {}

export function SidebarEmptyState({ ref, ...props }: SidebarEmptyState) {
    return (
        <p
            ref={ref}
            {...props}
            className={clsx('mx-1.5 text-ui-small font-normal text-muted', props.className)}
        />
    );
}
